import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Switch } from '../ui/switch';
import { Bell, Clock, Mail, Send, Save, RefreshCw, Loader2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

export function BillingSettingsTab({
  billingSettings, setBillingSettings, onSave,
  schedulerStatus, loadSchedulerStatus, loadingScheduler,
  pendingReminders, onProcessReminders, processingReminders,
  onOpenTestEmail,
}) {
  return (
    <div className="max-w-3xl">
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-[#1e3a8a]" style={{ fontFamily: 'Merriweather, serif' }}>Paramètres de facturation</CardTitle>
          <CardDescription>Configurez les informations affichées sur les devis et factures</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSave} className="space-y-6">
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-800">Informations de l'organisation</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nom de l'organisation</Label>
                  <Input value={billingSettings.company_name} onChange={(e) => setBillingSettings({ ...billingSettings, company_name: e.target.value })} data-testid="billing-company-name" />
                </div>
                <div className="space-y-2">
                  <Label>SIRET</Label>
                  <Input value={billingSettings.company_siret} onChange={(e) => setBillingSettings({ ...billingSettings, company_siret: e.target.value })} data-testid="billing-siret" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Adresse</Label>
                <Textarea value={billingSettings.company_address} onChange={(e) => setBillingSettings({ ...billingSettings, company_address: e.target.value })} rows={2} data-testid="billing-address" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Téléphone</Label>
                  <Input value={billingSettings.company_phone} onChange={(e) => setBillingSettings({ ...billingSettings, company_phone: e.target.value })} data-testid="billing-phone" />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" value={billingSettings.company_email} onChange={(e) => setBillingSettings({ ...billingSettings, company_email: e.target.value })} data-testid="billing-email" />
                </div>
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-semibold text-slate-800">Conditions</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Validité des devis (jours)</Label>
                  <Input type="number" value={billingSettings.quote_validity_days} onChange={(e) => setBillingSettings({ ...billingSettings, quote_validity_days: parseInt(e.target.value) })} min="1" data-testid="billing-quote-validity" />
                </div>
                <div className="space-y-2">
                  <Label>Délai de paiement (jours)</Label>
                  <Input type="number" value={billingSettings.payment_terms_days} onChange={(e) => setBillingSettings({ ...billingSettings, payment_terms_days: parseInt(e.target.value) })} min="1" data-testid="billing-payment-terms" />
                </div>
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-semibold text-slate-800">Textes personnalisés</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Pied de page des devis</Label>
                  <Textarea value={billingSettings.quote_footer_text} onChange={(e) => setBillingSettings({ ...billingSettings, quote_footer_text: e.target.value })} rows={3} placeholder="Conditions générales, mentions légales..." data-testid="billing-quote-footer" />
                </div>
                <div className="space-y-2">
                  <Label>Pied de page des appels à participation</Label>
                  <Textarea value={billingSettings.invoice_footer_text} onChange={(e) => setBillingSettings({ ...billingSettings, invoice_footer_text: e.target.value })} rows={3} placeholder="Mentions légales..." data-testid="billing-invoice-footer" />
                </div>
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t">
              <h4 className="font-semibold text-[#1e3a8a]">Informations de règlement</h4>
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label>Nom du bénéficiaire du virement</Label>
                  <Input value={billingSettings.bank_beneficiary_name || ''} onChange={(e) => setBillingSettings({ ...billingSettings, bank_beneficiary_name: e.target.value })} placeholder="Association Diocésaine de Pamiers" data-testid="billing-bank-beneficiary" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>IBAN</Label>
                    <Input value={billingSettings.iban || ''} onChange={(e) => setBillingSettings({ ...billingSettings, iban: e.target.value })} placeholder="FR76 1234 5678 9012 3456 7890 123" className="font-mono" data-testid="billing-iban" />
                  </div>
                  <div className="space-y-2">
                    <Label>BIC / SWIFT</Label>
                    <Input value={billingSettings.bic || ''} onChange={(e) => setBillingSettings({ ...billingSettings, bic: e.target.value })} placeholder="BNPAFRPP" className="font-mono" data-testid="billing-bic" />
                  </div>
                </div>
                <p className="text-xs text-slate-500">Ces informations seront affichées dans un encadré sur les appels à participation</p>
                <div className="space-y-2">
                  <Label>Conditions de paiement (police réduite)</Label>
                  <Textarea value={billingSettings.payment_conditions || ''} onChange={(e) => setBillingSettings({ ...billingSettings, payment_conditions: e.target.value })} rows={3} placeholder="Tout règlement doit être effectué à réception de l'appel à participation..." className="text-sm" data-testid="billing-payment-conditions" />
                  <p className="text-xs text-slate-500">Affiché en bas de page en petits caractères</p>
                </div>
              </div>
            </div>

            <div className="space-y-4 pt-6 border-t">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                    <Bell className="h-5 w-5 text-amber-600" /> Relances automatiques
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">Configuration des emails de relance envoyés automatiquement</p>
                </div>
                <div className="flex items-center gap-2">
                  <Switch id="auto-reminders" checked={billingSettings.auto_reminders_enabled !== false} onCheckedChange={(checked) => setBillingSettings({ ...billingSettings, auto_reminders_enabled: checked })} data-testid="auto-reminders-toggle" />
                  <Label htmlFor="auto-reminders" className="text-sm">{billingSettings.auto_reminders_enabled !== false ? 'Activé' : 'Désactivé'}</Label>
                </div>
              </div>

              {billingSettings.auto_reminders_enabled !== false && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-4">
                  <p className="text-sm text-amber-800">Les relances sont envoyées automatiquement chaque jour à 8h00 pour les factures en retard.</p>
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      { key: 'reminder_j7_enabled', label: 'J+7', desc: 'Premier rappel', testid: 'reminder-j7-toggle' },
                      { key: 'reminder_j14_enabled', label: 'J+14', desc: 'Deuxième rappel', testid: 'reminder-j14-toggle' },
                      { key: 'reminder_j21_enabled', label: 'J+21', desc: 'Dernier rappel', testid: 'reminder-j21-toggle' },
                    ].map(r => (
                      <div key={r.key} className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200">
                        <div>
                          <span className="font-medium text-sm">{r.label}</span>
                          <p className="text-xs text-slate-500">{r.desc}</p>
                        </div>
                        <Switch checked={billingSettings[r.key] !== false} onCheckedChange={(checked) => setBillingSettings({ ...billingSettings, [r.key]: checked })} data-testid={r.testid} />
                      </div>
                    ))}
                  </div>

                  {schedulerStatus && (
                    <div className="mt-4 p-3 bg-white rounded-lg border border-slate-200">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-slate-700 flex items-center gap-2"><Clock className="h-4 w-4" /> État du planificateur</h4>
                        <Button variant="outline" size="sm" onClick={loadSchedulerStatus} disabled={loadingScheduler}>
                          <RefreshCw className={`h-3 w-3 mr-1 ${loadingScheduler ? 'animate-spin' : ''}`} /> Actualiser
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-slate-500">Prochaine exécution :</span>
                          <p className="font-medium">{schedulerStatus.next_run ? format(parseISO(schedulerStatus.next_run), "d MMM yyyy 'à' HH:mm", { locale: fr }) : '-'}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Dernière exécution :</span>
                          <p className="font-medium">{schedulerStatus.last_run?.timestamp ? format(parseISO(schedulerStatus.last_run.timestamp), "d MMM yyyy 'à' HH:mm", { locale: fr }) : 'Jamais'}</p>
                        </div>
                      </div>
                      {schedulerStatus.last_run && (
                        <div className="mt-2 text-xs text-slate-500">
                          Dernier résultat : {schedulerStatus.last_run.status === 'success' ? `${schedulerStatus.last_run.reminders_sent || 0} relance(s) envoyée(s)` : schedulerStatus.last_run.status === 'skipped' ? 'Relances désactivées' : `Erreur: ${schedulerStatus.last_run.error || 'Inconnue'}`}
                        </div>
                      )}
                      <div className="mt-3 pt-3 border-t border-slate-200 flex items-center justify-between">
                        <div className="text-xs text-slate-500"><span className="font-medium">Limite :</span> 15 emails/min</div>
                        <Button variant="outline" size="sm" onClick={onOpenTestEmail} data-testid="test-reminder-email"><Mail className="h-3 w-3 mr-1" /> Tester l'envoi</Button>
                      </div>
                    </div>
                  )}

                  {pendingReminders && pendingReminders.total_pending > 0 && (
                    <div className="mt-4 p-3 bg-white rounded-lg border border-orange-200">
                      <h4 className="text-sm font-medium text-orange-700 mb-2">{pendingReminders.total_pending} relance(s) en attente</h4>
                      <div className="text-xs text-slate-600 space-y-1">
                        {pendingReminders.pending_j7?.length > 0 && <p>J+7 : {pendingReminders.pending_j7.length} facture(s)</p>}
                        {pendingReminders.pending_j14?.length > 0 && <p>J+14 : {pendingReminders.pending_j14.length} facture(s)</p>}
                        {pendingReminders.pending_j21?.length > 0 && <p>J+21 : {pendingReminders.pending_j21.length} facture(s)</p>}
                      </div>
                      <Button variant="outline" size="sm" className="mt-3 text-orange-700 border-orange-300 hover:bg-orange-50" onClick={onProcessReminders} disabled={processingReminders} data-testid="process-reminders-now">
                        {processingReminders ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Traitement...</> : <><Send className="h-3 w-3 mr-1" /> Traiter maintenant</>}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <Button type="submit" className="bg-[#1e3a8a] hover:bg-[#1e40af]" data-testid="save-billing-button">
              <Save className="mr-2 h-4 w-4" /> Enregistrer
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
